import {ChangeDetectorRef, Component, OnDestroy, OnInit} from '@angular/core';
import {AnimalRepository} from '../animals/states/animal.repository';
import {TranslocoService} from '@jsverse/transloco';
import {AnimalAddService} from '../animals/services/animal-add.service';
import { take, takeWhile } from 'rxjs/operators';
import {AnimalModel} from '../animals/models/animal.model';
import {AnimalOriginReasonForTransfer} from '../animals/models/animal-origin-reason-for-transfer.enum';
import {AnimalWhereaboutsType} from '../animals/models/animal-wherabout-types.enum';
import {AnimalWhereaboutsRepository} from "../animals/states/animal-whereabouts.repository";
import {AnimalOriginRepository} from '../animals/states/animal-origin.repository';
import {HelperService} from "../../shared/services/helper.service";
import {ContactRepository} from '../contact/states/contact.repository';
import {DashboardLoadingService} from 'src/app/shared/services/dashboard-loading.service';
import { combineLatest, forkJoin } from 'rxjs';

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
  standalone: false
})
export class DashboardComponent implements OnInit, OnDestroy {
  alive: boolean = true;
  value = 0;
  data: any;
  options: any;
  addDialogVisible: boolean = false;
  newAnimals: AnimalModel[] = [];
  maxLastEditedAnimals = 20;
  whereabouts: any;

  loadingNewAnimals = false;

  pendingOriginAnimals: AnimalModel[] = [];
  pendingSecuredByPoliceAnimals: AnimalModel[] = [];
  pendingWhereaboutAnimals: AnimalModel[] = [];
  loadingPendingOrigins = false;
  loadingPendingSecuredByPolice = false;
  loadingPendingWhereabouts = false;

  constructor(
    private readonly _animalRepository: AnimalRepository,
    private readonly _animalWhereaboutsRepository: AnimalWhereaboutsRepository,
    private readonly _animalOriginRepository: AnimalOriginRepository,
    private readonly _translocoService: TranslocoService,
    private readonly _animalAddService: AnimalAddService,
    private readonly _helperService: HelperService,
    private readonly _contactRepository: ContactRepository,
    private readonly _cdr: ChangeDetectorRef,
    private readonly _dashboardLoadingService: DashboardLoadingService,
  ) {
    // silence is golden
  }

  ngOnInit(): void {
    this._dashboardLoadingService.loading$.subscribe(loading => {
      this.loadingNewAnimals = loading;
      this._cdr.detectChanges();
    });

    this.loadPendingReports();

    this._animalAddService.addDialogVisible.pipe(takeWhile(() => this.alive)).subscribe((openModal) => {
      this.addDialogVisible = openModal;
    });

    this._animalRepository.getAnimals$().pipe(takeWhile(() => this.alive)).subscribe((animals: AnimalModel[]) => {
      const categoryCounts: { [key: string]: number } = animals.reduce((counts: { [key: string]: number }, animal) => {
        if (animal.category !== null) {
          if (!counts[this._translocoService.translate('animal.category.' + animal.category)]) {
            counts[this._translocoService.translate('animal.category.' + animal.category)] = 0;
          }
          counts[this._translocoService.translate('animal.category.' + animal.category)]++;
        }
        return counts;
      }, {});

      this.data = {
        labels: Object.keys(categoryCounts),
        datasets: [
          {
            data: Object.values(categoryCounts)
          }
        ]
      };

      if (animals.length > 0) {
        this.getLastEditedAnimals(animals);
      } else {
        this.newAnimals = [];
      }

      this._cdr.detectChanges();
    });

  }


  getDateFormat(): string {
    return this._helperService.getDateStringByLang();
  }

  getLastEditedAnimals(animals: AnimalModel[]): void {
    if (!animals || animals.length === 0) {
      return;
    }
    const sortedAnimals = animals.sort((a, b) => {
      const dateA = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
      const dateB = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
      return dateB - dateA;
    });

    this.newAnimals = sortedAnimals.slice(0, this.maxLastEditedAnimals);

    this.loadWhereaboutsForAnimals(this.newAnimals);
  }

  loadWhereaboutsForAnimals(animals: AnimalModel[]): void {
    if (!this.whereabouts) {
      this.whereabouts = new Map<number, any>();
    }

    animals.forEach((animal) => {
      this._animalWhereaboutsRepository.getByAnimalId(animal.id).pipe(take(1)).subscribe((whereabouts: any) => {
        if (Array.isArray(whereabouts) && whereabouts.length > 0) {
          const latestWhereabout = whereabouts.reduce((prev, current) => (prev.id > current.id ? prev : current));

          if (latestWhereabout.contact?.id) {
            this._contactRepository.getById(latestWhereabout.contact.id).pipe(take(1)).subscribe((contact) => {
              if (contact.city) {
                latestWhereabout.location = contact.city;
              }
              this.whereabouts.set(animal.id, latestWhereabout);
            });
          } else {
            this.whereabouts.set(animal.id, latestWhereabout);
          }
        }
      });
    });
    this._cdr.detectChanges();
  }

  loadPendingReports(): void {
    this.loadingPendingOrigins = true;
    this.loadingPendingSecuredByPolice = true;
    this.loadingPendingWhereabouts = true;
    this._cdr.detectChanges();

    this._animalOriginRepository.refreshPendingReports();
    this._animalWhereaboutsRepository.refreshPendingReports();

    // Store-gebunden, damit neu erzeugte bzw. versendete PDFs ohne Reload erscheinen/verschwinden
    combineLatest([
      this._animalOriginRepository.selectPendingReports(),
      this._animalWhereaboutsRepository.selectPendingReports(),
    ]).pipe(takeWhile(() => this.alive)).subscribe(([origins, whereabouts]) => {
      const foundAnimalIds = this.collectAnimalIds(origins, (o) => o.type === AnimalOriginReasonForTransfer.FOUND_ANIMAL);
      const securedByPoliceIds = this.collectAnimalIds(origins, (o) => o.type === AnimalOriginReasonForTransfer.SECURED_BY_POLICE);
      const whereaboutReturnIds = this.collectAnimalIds(whereabouts, (w) => w.type === AnimalWhereaboutsType.FOUNDANIMAL_RETURN);

      this.loadAnimalsByIds(
        foundAnimalIds,
        (animals) => this.pendingOriginAnimals = animals,
        () => this.loadingPendingOrigins = false,
      );

      this.loadAnimalsByIds(
        securedByPoliceIds,
        (animals) => this.pendingSecuredByPoliceAnimals = animals,
        () => this.loadingPendingSecuredByPolice = false,
      );

      this.loadAnimalsByIds(
        whereaboutReturnIds,
        (animals) => this.pendingWhereaboutAnimals = animals,
        () => this.loadingPendingWhereabouts = false,
      );
    });
  }

  private collectAnimalIds(reports: any[], predicate: (report: any) => boolean): number[] {
    return Array.from(new Set(
      reports.filter(predicate).map((report: any) => report.animal?.id).filter(Boolean)
    )) as number[];
  }

  private loadAnimalsByIds(
    ids: number[],
    assign: (animals: AnimalModel[]) => void,
    done: () => void,
  ): void {
    if (!ids.length) {
      assign([]);
      done();
      this._cdr.detectChanges();
      return;
    }

    forkJoin(ids.map((id: number) => this._animalRepository.getById(id, false).pipe(take(1))))
      .pipe(take(1))
      .subscribe((animals) => {
        assign(animals.filter(Boolean) as AnimalModel[]);
        done();
        this._cdr.detectChanges();
      });
  }

  toggleAddDialog() {
    // sicherstellen, dass kein altes resumeAnimal gesetzt ist, wenn normaler Add-Dialog geöffnet wird
    this._animalAddService.setResumeAnimal(null);
    this._animalAddService.toggleAddDialog(true);
  }

  ngOnDestroy(): void {
    this.alive = false;
  }
}
